import { useState } from 'react';
import type { ReactNode } from 'react';
import { CheckIcon, PlusIcon, XIcon } from 'lucide-react';
import {
  CANONICAL_SPECIALISATIONS,
  CARE_LEVEL_LABELS,
  specialisationLabel,
  classifyLegacyLocation,
  type CareLevel,
  type LocationType,
  type Provider,
} from '@kennisbank/shared';
import { ApiError } from '../api/client.js';

/**
 * Provider create / edit dialog — shared between the admin Beheer page
 * (full CRUD list) and the practitioner Sociale Kaart page (admin
 * click-to-edit on a card). Mounts as a fixed-position modal; caller
 * controls open/close + provides the onSubmit callback that talks to
 * the API.
 *
 * Edit mode shows two save buttons (B1 from /plan-eng-review):
 *   - "Verifieer & opslaan" → bumpVerified=true (stamps lastVerifiedAt)
 *   - "Alleen correctie"     → bumpVerified=false (preserves the trust date)
 *
 * Create mode shows a single "Aanmaken" button.
 */

const TYPE_OPTIONS: Provider['type'][] = [
  'ggz_instelling',
  'vrijgevestigd',
  'groepspraktijk',
  'e_health',
  'overig',
];
const LOCATION_TYPE_OPTIONS: { value: LocationType; label: string }[] = [
  { value: 'fysiek', label: 'Fysieke praktijk (vast adres)' },
  { value: 'online', label: 'Alleen online / telefonisch' },
  { value: 'meerdere_locaties', label: 'Meerdere locaties' },
  { value: 'onbekend', label: 'Adres onbekend' },
];
const STATUS_OPTIONS: Provider['status'][] = ['active', 'closed', 'unknown'];
const AUDIENCE_OPTIONS: Provider['targetAudience'][number][] = [
  'volwassenen',
  'jongeren',
  'jeugd',
  'ouderen',
  'paren',
  'gezinnen',
  'vrouwen',
];
const CARE_LEVEL_OPTIONS = Object.entries(CARE_LEVEL_LABELS) as [CareLevel, string][];
// 'lvvp' included so editing a crawler-ingested record round-trips its source
// (admins won't normally pick it manually). The crawler stops touching an
// lvvp record once an admin edits it - see ProviderService.update auto-lock.
const SOURCE_OPTIONS: Provider['source'][] = [
  'suppohrt_manual',
  'zorgkaart_nl',
  'external',
  'lvvp',
];

const THERAPY_OPTIONS = [
  'CGT',
  'ACT',
  'EMDR',
  'EFT',
  'Schematherapie',
  'Mindfulness',
  'MBCT',
  'Systeemtherapie',
  'Speltherapie',
  'Haptotherapie',
  'Psychotherapie',
  'Hypnotherapie',
  'Oplossingsgericht',
];

/**
 * Eén extra vestiging in het formulier. Altijd fysiek (een extra vestiging heeft
 * per definitie een adres — dat is het hele punt: een eigen pin op de kaart). Voor
 * een niet-fysiek aanbod is het primaire locatietype al genoeg.
 */
export interface AdditionalLocationForm {
  address: string;
  city: string;
  region: string;
  province: string;
}

export interface ProviderFormState {
  name: string;
  type: Provider['type'];
  locationType: LocationType;
  address: string;
  /** Extra vestigingen (multi-locatie); elk krijgt een eigen kaartpin. */
  additionalLocations: AdditionalLocationForm[];
  /**
   * Alleen UI: de oude adreswaarde die geen bruikbaar adres was en dus bij
   * opslaan verdwijnt. Wordt NIET meegestuurd — bestaat om het verlies zichtbaar
   * te maken i.p.v. het stil te laten gebeuren.
   */
  legacyAddressDropped?: string;
  city: string;
  region: string;
  province: string;
  specialisations: string[];
  therapyTypes: string[];
  targetAudience: Provider['targetAudience'][number][];
  careLevels: CareLevel[];
  noReferralNeeded: boolean;
  phone: string;
  email: string;
  website: string;
  status: Provider['status'];
  waitingTime: string;
  notes: string;
  source: Provider['source'];
}

export const EMPTY_PROVIDER_FORM: ProviderFormState = {
  name: '',
  type: 'vrijgevestigd',
  locationType: 'fysiek',
  address: '',
  additionalLocations: [],
  city: '',
  region: '',
  province: '',
  specialisations: [],
  therapyTypes: [],
  targetAudience: ['volwassenen'],
  careLevels: [],
  noReferralNeeded: false,
  phone: '',
  email: '',
  website: '',
  status: 'active',
  waitingTime: '',
  notes: '',
  source: 'suppohrt_manual',
};

export function providerToForm(p: Provider): ProviderFormState {
  // Pre-migratie records dragen nog geen locationType. Dan tonen we de gok van
  // de gedeelde classifier, zodat de admin bevestigt of corrigeert in plaats van
  // een validatiefout te krijgen op data die hij niet zelf heeft ingevoerd.
  const guess = p.location.locationType
    ? { locationType: p.location.locationType, address: p.location.address, city: p.location.city }
    : classifyLegacyLocation(p.location);
  // Had dit record een adresstring die de classifier weggooit? Dan moet de admin
  // dat ZIEN. Anders verliest 'Praktijk Broeksma' zijn enige adresgegeven zodra
  // iemand een ongerelateerde specialisatie bijwerkt en opslaat.
  const dropped =
    !p.location.locationType && p.location.address && !guess.address
      ? p.location.address
      : undefined;
  return {
    name: p.name,
    type: p.type,
    locationType: guess.locationType,
    address: guess.address ?? '',
    // Bestaande extra vestigingen round-trippen; leeg als het record er geen heeft.
    additionalLocations: (p.additionalLocations ?? []).map((l) => ({
      address: l.address ?? '',
      city: l.city ?? '',
      region: l.region,
      province: l.province,
    })),
    city: guess.city ?? '',
    ...(dropped ? { legacyAddressDropped: dropped } : {}),
    region: p.location.region,
    province: p.location.province,
    specialisations: [...p.specialisations],
    therapyTypes: [...p.therapyTypes],
    targetAudience: [...p.targetAudience],
    careLevels: [...(p.careLevels ?? [])],
    noReferralNeeded: p.noReferralNeeded ?? false,
    phone: p.contactInfo.phone ?? '',
    email: p.contactInfo.email ?? '',
    website: p.contactInfo.website ?? '',
    status: p.status,
    waitingTime: p.waitingTime ?? '',
    notes: p.notes ?? '',
    source: p.source,
  };
}

export function formToPayload(f: ProviderFormState): Record<string, unknown> {
  const contactInfo: { phone?: string; email?: string; website?: string } = {};
  if (f.phone) contactInfo.phone = f.phone;
  if (f.email) contactInfo.email = f.email;
  if (f.website) contactInfo.website = f.website;
  // Niet-fysiek: adres en plaats bewust weglaten i.p.v. leegstring meesturen —
  // het schema weigert een adres bij een niet-fysiek type.
  const fysiek = f.locationType === 'fysiek';
  // Extra vestigingen: altijd fysiek. Lege rijen (geen adres of plaats) vallen af.
  // We sturen de array ALTIJD mee (ook leeg), zodat je bij bewerken vestigingen kunt
  // verwijderen — de PATCH-route vervangt de lijst alleen als het veld aanwezig is.
  const additionalLocations = f.additionalLocations
    .filter((l) => l.address.trim() && l.city.trim())
    .map((l) => ({
      locationType: 'fysiek' as const,
      address: l.address.trim(),
      city: l.city.trim(),
      region: l.region.trim() || l.city.trim() || l.province.trim(),
      province: l.province.trim(),
    }));
  return {
    name: f.name,
    type: f.type,
    location: {
      locationType: f.locationType,
      ...(fysiek && f.address ? { address: f.address } : {}),
      ...(f.city ? { city: f.city } : {}),
      region: f.region || f.city || f.province,
      province: f.province,
    },
    additionalLocations,
    specialisations: f.specialisations,
    therapyTypes: f.therapyTypes,
    targetAudience: f.targetAudience,
    careLevels: f.careLevels,
    noReferralNeeded: f.noReferralNeeded,
    contactInfo,
    status: f.status,
    waitingTime: f.waitingTime ? f.waitingTime : null,
    ...(f.notes ? { notes: f.notes } : {}),
    source: f.source,
    lastVerifiedAt: new Date().toISOString(),
  };
}

/**
 * Zod-validatiefouten komen binnen als `{ formErrors, fieldErrors }` achter een
 * generieke Engelse kop. De veldboodschappen zelf zijn Nederlands en bruikbaar —
 * die willen we tonen, niet een JSON.stringify van het hele blob.
 */
function apiErrorText(err: ApiError): string {
  const d = err.detail as
    | { formErrors?: string[]; fieldErrors?: Record<string, string[]> }
    | undefined;
  const zod = [...(d?.formErrors ?? []), ...Object.values(d?.fieldErrors ?? {}).flat()];
  return zod.length > 0 ? zod.join(' ') : err.message;
}

export interface ProviderFormSubmitOpts {
  bumpVerified: boolean;
}

export function ProviderFormDialog({
  title,
  initial,
  onClose,
  onSubmit,
  mode,
  variant = 'admin',
}: {
  title: string;
  initial: ProviderFormState;
  onClose: () => void;
  onSubmit: (form: ProviderFormState, opts: ProviderFormSubmitOpts) => Promise<void>;
  mode: 'create' | 'edit';
  /**
   * 'suggestion' = POH stelt een praktijk voor (wacht op admin-goedkeuring):
   * verbergt de admin-velden Status + Bron (die dwingt de server af) en labelt
   * de knop als "Ter goedkeuring indienen". 'admin' = volledige CRUD.
   */
  variant?: 'admin' | 'suggestion';
}): JSX.Element {
  const isSuggestion = variant === 'suggestion';
  const [form, setForm] = useState<ProviderFormState>(initial);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function update<K extends keyof ProviderFormState>(key: K, value: ProviderFormState[K]): void {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function setLoc<K extends keyof AdditionalLocationForm>(i: number, key: K, value: string): void {
    update(
      'additionalLocations',
      form.additionalLocations.map((l, j) => (j === i ? { ...l, [key]: value } : l)),
    );
  }

  async function submit(opts: ProviderFormSubmitOpts): Promise<void> {
    // Een half ingevulde extra vestiging (wel adres, geen plaats/provincie of
    // omgekeerd) zou anders stil door de filter in formToPayload wegvallen —
    // data-verlies zonder melding. Blokkeer met een leesbare fout i.p.v. de rij
    // te droppen. Een volledig lege rij is prima (die negeren we bewust).
    const badLoc = form.additionalLocations.findIndex((l) => {
      const hasAny = l.address.trim() || l.city.trim() || l.region.trim() || l.province.trim();
      const complete = l.address.trim() && l.city.trim() && l.province.trim();
      return hasAny && !complete;
    });
    if (badLoc >= 0) {
      setError(
        `Vestiging ${badLoc + 1}: vul adres, plaats én provincie in, of verwijder de vestiging.`,
      );
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      await onSubmit(form, opts);
    } catch (err) {
      setError(err instanceof ApiError ? apiErrorText(err) : 'Opslaan mislukte');
    } finally {
      setSubmitting(false);
    }
  }

  // z-50: boven de filterbalk en app-sidebar (beide z-40) op de Sociale Kaart,
  // anders schuift de filterrail over deze edit-modal heen.
  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-ink-900/40 p-6"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="w-full max-w-2xl rounded-2xl bg-white p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <header className="mb-4 flex items-start justify-between border-b border-brand-50 pb-3">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wider text-ink-800/50">
              {isSuggestion
                ? 'Praktijk voorstellen'
                : mode === 'create'
                  ? 'Nieuwe aanbieder'
                  : 'Aanbieder bewerken'}
            </div>
            <h2 className="font-display text-lg font-bold text-ink-900">{title}</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-ink-900/40 transition hover:text-ink-900/80"
            aria-label="Sluiten"
          >
            ✕
          </button>
        </header>

        {isSuggestion && (
          <p className="mb-4 rounded-md bg-brand-50 px-3 py-2 text-sm text-brand-800">
            Je voorstel gaat eerst naar een SUPPOHRT-beheerder ter goedkeuring. Na akkoord
            verschijnt de praktijk in de Sociale Kaart.
          </p>
        )}

        {/* Top error pane — visible when the form fits without scrolling.
            We also render a sticky copy above the action buttons (below)
            so the message is impossible to miss on a long form. */}
        {error && (
          <p
            role="alert"
            aria-live="assertive"
            className="mb-4 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700"
          >
            {error}
          </p>
        )}

        {form.legacyAddressDropped && (
          <p className="mb-4 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800">
            Dit record heeft nog geen locatietype. De huidige adreswaarde{' '}
            <strong>&ldquo;{form.legacyAddressDropped}&rdquo;</strong> is geen bruikbaar adres en
            verdwijnt zodra je opslaat. Kies het juiste locatietype, of zet het locatietype op
            &ldquo;Fysieke praktijk&rdquo; en vul een adres met huisnummer in.
          </p>
        )}

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Naam" required>
            <Input value={form.name} onChange={(v) => update('name', v)} />
          </Field>
          <Field label="Type">
            <Select
              value={form.type}
              onChange={(v) => update('type', v as Provider['type'])}
              options={TYPE_OPTIONS.map((t) => ({ value: t, label: t }))}
            />
          </Field>
          <Field label="Locatietype" required>
            <Select
              value={form.locationType}
              onChange={(v) => update('locationType', v as LocationType)}
              options={LOCATION_TYPE_OPTIONS}
            />
          </Field>
          <Field
            label="Adres"
            required={form.locationType === 'fysiek'}
            hint={
              form.locationType === 'fysiek'
                ? 'Straat + huisnummer, of postcode.'
                : 'Niet van toepassing bij dit locatietype.'
            }
          >
            <Input
              value={form.locationType === 'fysiek' ? form.address : ''}
              onChange={(v) => update('address', v)}
              disabled={form.locationType !== 'fysiek'}
            />
          </Field>
          <Field label="Plaats" required={form.locationType === 'fysiek'}>
            <Input value={form.city} onChange={(v) => update('city', v)} />
          </Field>
          <Field label="Regio">
            <Input value={form.region} onChange={(v) => update('region', v)} />
          </Field>
          <Field label="Provincie" required>
            <Input value={form.province} onChange={(v) => update('province', v)} />
          </Field>

          <div className="sm:col-span-2">
            <div className="mb-1.5 flex items-center justify-between">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-ink-800/60">
                Extra vestigingen
              </span>
              <button
                type="button"
                onClick={() =>
                  update('additionalLocations', [
                    ...form.additionalLocations,
                    { address: '', city: '', region: '', province: form.province },
                  ])
                }
                className="inline-flex items-center gap-1 rounded-md border border-brand-200 px-2 py-1 text-[11px] font-medium text-brand-700 transition-colors hover:bg-brand-50"
              >
                <PlusIcon size={12} /> Vestiging toevoegen
              </button>
            </div>
            <p className="mb-2 text-[11px] font-normal normal-case tracking-normal text-ink-800/50">
              Voor organisaties met meerdere locaties (bv. Arkin, Spoor8). Elke vestiging krijgt een
              eigen pin op de kaart.
            </p>
            {form.additionalLocations.length === 0 ? (
              <p className="rounded-md border border-dashed border-brand-200 px-3 py-2 text-[12px] font-normal normal-case tracking-normal text-ink-800/45">
                Nog geen extra vestigingen.
              </p>
            ) : (
              <div className="space-y-2">
                {form.additionalLocations.map((loc, i) => (
                  <div key={i} className="rounded-lg border border-brand-100 bg-brand-50/30 p-3">
                    <div className="mb-2 flex items-center justify-between">
                      <span className="text-[11px] font-semibold uppercase tracking-wider text-ink-800/60">
                        Vestiging {i + 1}
                      </span>
                      <button
                        type="button"
                        onClick={() =>
                          update(
                            'additionalLocations',
                            form.additionalLocations.filter((_, j) => j !== i),
                          )
                        }
                        aria-label={`Verwijder vestiging ${i + 1}`}
                        className="rounded-md p-1 text-ink-900/40 transition-colors hover:bg-red-50 hover:text-red-600"
                      >
                        <XIcon size={13} />
                      </button>
                    </div>
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                      <Field label="Adres" required>
                        <Input
                          value={loc.address}
                          onChange={(v) => setLoc(i, 'address', v)}
                          placeholder="Straat + huisnummer"
                        />
                      </Field>
                      <Field label="Plaats" required>
                        <Input value={loc.city} onChange={(v) => setLoc(i, 'city', v)} />
                      </Field>
                      <Field label="Regio">
                        <Input value={loc.region} onChange={(v) => setLoc(i, 'region', v)} />
                      </Field>
                      <Field label="Provincie">
                        <Input value={loc.province} onChange={(v) => setLoc(i, 'province', v)} />
                      </Field>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <Field label="Specialisaties" full>
            <ChipMultiSelect
              options={[...CANONICAL_SPECIALISATIONS]}
              value={form.specialisations}
              onChange={(v) => update('specialisations', v)}
              addPlaceholder="Voeg eigen specialisatie toe…"
            />
          </Field>
          <Field label="Therapievormen" full>
            <ChipMultiSelect
              options={THERAPY_OPTIONS}
              value={form.therapyTypes}
              onChange={(v) => update('therapyTypes', v)}
              addPlaceholder="Voeg eigen therapievorm toe…"
            />
          </Field>
          <Field label="Doelgroep" full>
            <div className="flex flex-wrap gap-2 text-sm">
              {AUDIENCE_OPTIONS.map((a) => {
                const checked = form.targetAudience.includes(a);
                return (
                  <button
                    key={a}
                    type="button"
                    onClick={() =>
                      update(
                        'targetAudience',
                        checked
                          ? form.targetAudience.filter((x) => x !== a)
                          : [...form.targetAudience, a],
                      )
                    }
                    className={`rounded-full border px-2.5 py-1 text-[12px] transition-colors ${
                      checked
                        ? 'border-brand-500 bg-brand-500 text-white'
                        : 'border-brand-200 bg-white text-ink-800 hover:border-brand-400'
                    }`}
                  >
                    {a}
                  </button>
                );
              })}
            </div>
          </Field>
          <Field label="Zorgniveau" full>
            <div className="flex flex-wrap items-center gap-2 text-sm">
              {CARE_LEVEL_OPTIONS.map(([c, label]) => {
                const checked = form.careLevels.includes(c);
                return (
                  <button
                    key={c}
                    type="button"
                    aria-pressed={checked}
                    onClick={() =>
                      update(
                        'careLevels',
                        checked ? form.careLevels.filter((x) => x !== c) : [...form.careLevels, c],
                      )
                    }
                    className={`rounded-full border px-2.5 py-1 text-[12px] transition-colors ${
                      checked
                        ? 'border-brand-700 bg-brand-700 text-white'
                        : 'border-brand-200 bg-white text-ink-800 hover:border-brand-400'
                    }`}
                  >
                    {label}
                  </button>
                );
              })}
              <label className="ml-2 inline-flex items-center gap-1.5 text-[12px] text-ink-800">
                <input
                  type="checkbox"
                  checked={form.noReferralNeeded}
                  onChange={(e) => update('noReferralNeeded', e.target.checked)}
                />
                Zonder verwijzing toegankelijk
              </label>
            </div>
          </Field>
          <Field label="Telefoon">
            <Input value={form.phone} onChange={(v) => update('phone', v)} />
          </Field>
          <Field label="E-mail">
            <Input type="email" value={form.email} onChange={(v) => update('email', v)} />
          </Field>
          <Field label="Website" full>
            <Input
              type="url"
              value={form.website}
              onChange={(v) => update('website', v)}
              placeholder="https://"
            />
          </Field>
          {/* Wachttijd (vrije tekst) verwijderd 2026-05-08 — Linda houdt
              dit niet meer bij. Het veld blijft in de Provider-schema en
              in Firestore voor bestaande data, alleen niet meer
              redigeerbaar of zichtbaar in de UI. */}
          {/* Status + Bron zijn admin-curatievelden. Bij een POH-voorstel dwingt
              de server status='pending' + source='suppohrt_manual' af, dus dan weg. */}
          {!isSuggestion && (
            <>
              <Field label="Status">
                <Select
                  value={form.status}
                  onChange={(v) => update('status', v as Provider['status'])}
                  options={STATUS_OPTIONS.map((s) => ({ value: s, label: s }))}
                />
              </Field>
              <Field label="Bron">
                <Select
                  value={form.source}
                  onChange={(v) => update('source', v as Provider['source'])}
                  options={SOURCE_OPTIONS.map((s) => ({ value: s, label: s }))}
                />
              </Field>
            </>
          )}
          <Field label="Notities" full>
            <textarea
              value={form.notes}
              onChange={(e) => update('notes', e.target.value)}
              rows={2}
              className="w-full rounded-md border border-brand-200 bg-white px-2 py-1.5 text-sm focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-200/40"
            />
          </Field>
        </div>

        {/* Footer error — sticky next to the action buttons so a long form
            doesn't hide the failure message above the fold. Same content
            as the top pane; user sees whichever is in viewport. */}
        {error && (
          <p
            role="alert"
            className="mt-6 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
          >
            {error}
          </p>
        )}

        <footer className="mt-6 flex items-center justify-end gap-3 border-t border-brand-50 pt-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-4 py-2 text-sm text-ink-900/60 hover:bg-brand-50 hover:text-ink-900"
          >
            Annuleren
          </button>
          {mode === 'edit' && (
            <button
              type="button"
              onClick={() => void submit({ bumpVerified: false })}
              disabled={submitting}
              className="rounded-lg border border-brand-200 px-4 py-2 text-sm font-medium text-ink-800 transition-colors hover:bg-brand-50 disabled:opacity-60"
              title="Sla wijzigingen op zonder de 'geverifieerd op'-datum bij te werken"
            >
              Alleen correctie
            </button>
          )}
          <button
            type="button"
            onClick={() => void submit({ bumpVerified: true })}
            disabled={submitting}
            className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-700 disabled:opacity-60"
          >
            {submitting
              ? 'Opslaan…'
              : isSuggestion
                ? 'Ter goedkeuring indienen'
                : mode === 'create'
                  ? 'Aanmaken'
                  : 'Verifieer & opslaan'}
          </button>
        </footer>
      </div>
    </div>
  );
}

function Field({
  label,
  required,
  full,
  hint,
  children,
}: {
  label: string;
  required?: boolean;
  full?: boolean;
  hint?: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <label
      className={`block text-[11px] font-semibold uppercase tracking-wider text-ink-800/60 ${
        full ? 'sm:col-span-2' : ''
      }`}
    >
      <span className="mb-1 block">
        {label}
        {required && <span className="text-red-500"> *</span>}
      </span>
      {children}
      {hint && (
        <span className="mt-1 block font-normal normal-case tracking-normal text-ink-800/50">
          {hint}
        </span>
      )}
    </label>
  );
}

function Input({
  value,
  onChange,
  type = 'text',
  placeholder,
  disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
  disabled?: boolean;
}): JSX.Element {
  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      disabled={disabled}
      className="w-full rounded-md border border-brand-200 bg-white px-2 py-1.5 text-sm font-normal normal-case tracking-normal text-ink-900 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-200/40 disabled:cursor-not-allowed disabled:bg-brand-50/60 disabled:text-ink-800/40"
    />
  );
}

function Select({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}): JSX.Element {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full rounded-md border border-brand-200 bg-white px-2 py-1.5 text-sm font-normal normal-case tracking-normal text-ink-900 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-200/40"
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

/**
 * ChipMultiSelect — picker for specialisations / therapy types.
 *
 * Renders the curated `options` as toggle chips (selected = brand-700
 * filled, unselected = outline). Any `value` already on the provider
 * that isn't in `options` (e.g. a free-text tag from the seed data)
 * shows as an amber "extra" chip with an inline × to remove. Below the
 * chips, an inline input lets admins add a brand new tag (Enter or +).
 * Duplicates (case-insensitive) are silently ignored.
 */
function ChipMultiSelect({
  options,
  value,
  onChange,
  addPlaceholder,
}: {
  options: string[];
  value: string[];
  onChange: (next: string[]) => void;
  addPlaceholder: string;
}): JSX.Element {
  const [draft, setDraft] = useState('');

  function toggle(opt: string): void {
    const present = value.find((v) => v.toLowerCase() === opt.toLowerCase());
    if (present) onChange(value.filter((v) => v !== present));
    else onChange([...value, opt]);
  }

  function addCustom(): void {
    const trimmed = draft.trim();
    if (!trimmed) return;
    if (value.some((v) => v.toLowerCase() === trimmed.toLowerCase())) {
      setDraft('');
      return;
    }
    onChange([...value, trimmed]);
    setDraft('');
  }

  const extras = value.filter((v) => !options.some((o) => o.toLowerCase() === v.toLowerCase()));

  return (
    <div className="space-y-2 normal-case font-normal tracking-normal">
      <div className="flex flex-wrap gap-1.5">
        {options.map((opt) => {
          const active = value.some((v) => v.toLowerCase() === opt.toLowerCase());
          return (
            <button
              key={opt}
              type="button"
              onClick={() => toggle(opt)}
              className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-[12px] font-medium transition-colors ${
                active
                  ? 'border-brand-700 bg-brand-700 text-white shadow-sm'
                  : 'border-brand-200 bg-white text-ink-800/80 hover:border-brand-400 hover:text-ink-900'
              }`}
            >
              {active && <CheckIcon size={11} strokeWidth={3} />}
              {specialisationLabel(opt)}
            </button>
          );
        })}
        {extras.map((extra) => (
          <span
            key={extra}
            className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 px-3 py-1 text-[12px] font-medium text-amber-900"
            title="Vrije-tekst tag, niet uit de standaardlijst. Klik × om te verwijderen, of laat staan als extra context op de kaart. Standaardlijst kan alleen door 48 Software worden uitgebreid."
          >
            {extra}
            <button
              type="button"
              onClick={() => onChange(value.filter((v) => v !== extra))}
              className="ml-0.5 -mr-1 rounded-full p-0.5 text-amber-900/60 hover:bg-amber-100 hover:text-amber-900"
              aria-label={`Verwijder ${extra}`}
            >
              <XIcon size={11} strokeWidth={2.5} />
            </button>
          </span>
        ))}
      </div>
      <div className="flex items-center gap-2">
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              addCustom();
            }
          }}
          placeholder={addPlaceholder}
          className="flex-1 rounded-md border border-brand-200 bg-white px-2 py-1.5 text-sm text-ink-900 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-200/40"
        />
        <button
          type="button"
          onClick={addCustom}
          disabled={!draft.trim()}
          className="inline-flex items-center gap-1 rounded-md border border-brand-200 px-3 py-1.5 text-xs font-medium text-ink-800 transition-colors hover:bg-brand-50 disabled:opacity-50"
        >
          <PlusIcon size={12} /> Toevoegen
        </button>
      </div>
    </div>
  );
}
